import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CLIENT_TIERS } from "@/lib/enumLabels";

/**
 * Inline partner-org (Client) editor for the Contact Directory.
 *
 * Iter 86.5 · Opens from the pencil next to a directory row's partner-
 * org column. Lets a partner edit Name / Tier / Industry / Relationship
 * health / Annual revenue without navigating to the Client detail page.
 *
 * Iter 86.5.1 · CRITICAL fix — the ContactDirectory row projection
 * only carries {client_name, client_tier, client_industry}. Seeding
 * the dialog from that alone forced `relationship_health` and
 * `annual_revenue` to hardcoded defaults, and `save()` PUT them
 * verbatim — silently overwriting the real health (and appending a
 * bogus health_history entry) on every save. Fix: fetch the full
 * client on open, snapshot the initial state, and only send fields
 * the partner actually changed. Local-first `onSaved` update lets the
 * caller propagate the new name/tier/industry across every row that
 * shares this client_id without a directory refetch.
 *
 * Iter 86.6 · Extracted from ContactDirectory.jsx to keep that file
 * under the 700-line guideline.
 */
export function ClientEditDialog({ row, industryOptions = [], onOpenChange, onSaved }) {
  const open = Boolean(row);
  const [form, setForm] = useState({ name: "", tier: "strategic", industry: "", relationship_health: "stable", annual_revenue: "" });
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!row?.id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/clients/${row.id}`).then(({ data }) => {
      if (cancelled || !data) return;
      const seed = {
        name: data.name || "",
        tier: data.tier || "strategic",
        industry: data.industry || "",
        relationship_health: data.relationship_health || "stable",
        annual_revenue: data.annual_revenue != null ? String(data.annual_revenue) : "",
      };
      setForm(seed);
      setOriginal(seed);
    }).catch(() => {
      // Fall back to the row-projection seed rather than block the
      // partner entirely; recorded as `original=null` so every changed
      // field is considered dirty.
      const seed = {
        name: row.name || "",
        tier: row.tier || "strategic",
        industry: row.industry || "",
        relationship_health: "stable",
        annual_revenue: "",
      };
      setForm(seed);
      setOriginal(null);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [row?.id]);
  const save = async () => {
    if (!row?.id || saving) return;
    const name = form.name.trim();
    if (!name) { toast.error("Partner org name is required"); return; }
    // Dirty-diff patch so unchanged enum fields (particularly
    // relationship_health) never round-trip and can't clobber a real
    // value with the form default.
    const patch = {};
    const orig = original || {};
    if (name !== (orig.name || "")) patch.name = name;
    if (form.tier !== (orig.tier || "strategic")) patch.tier = form.tier;
    if (form.relationship_health !== (orig.relationship_health || "stable")) {
      patch.relationship_health = form.relationship_health;
    }
    const industry = form.industry.trim();
    const origIndustry = (orig.industry || "").trim();
    if (industry !== origIndustry) {
      // Clearing the input clears the field on the server (explicit null).
      patch.industry = industry || null;
    }
    const origRevenueStr = orig.annual_revenue != null ? String(orig.annual_revenue) : "";
    if (form.annual_revenue !== origRevenueStr) {
      if (form.annual_revenue === "") {
        patch.annual_revenue = null;
      } else {
        const n = Number(form.annual_revenue);
        if (Number.isFinite(n)) patch.annual_revenue = n;
      }
    }
    if (Object.keys(patch).length === 0) {
      toast.message("No changes to save");
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${row.id}`, patch);
      onSaved(data || { ...(original || row), ...patch, id: row.id });
      toast.success("Partner organization updated");
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(" · ")
        : detail || "Save failed";
      toast.error(msg);
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-lg" data-testid="client-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">Edit partner organization</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {loading ? "Loading current values…" : "Changes propagate across every contact row that belongs to this partner org."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name">
            <Input data-testid="client-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none bg-secondary/40" disabled={loading} />
          </Field>
          <Field label="Tier">
            <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })} disabled={loading}>
              <SelectTrigger data-testid="client-edit-tier" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CLIENT_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Industry">
            <Input
              data-testid="client-edit-industry"
              list="client-edit-industry-list"
              value={form.industry}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              className="rounded-none bg-secondary/40"
              placeholder="e.g. Healthcare, Fintech"
              disabled={loading}
            />
            <datalist id="client-edit-industry-list">
              {industryOptions.map((v) => <option key={v} value={v} />)}
            </datalist>
          </Field>
          <Field label="Relationship health">
            <Select value={form.relationship_health} onValueChange={(v) => setForm({ ...form, relationship_health: v })} disabled={loading}>
              <SelectTrigger data-testid="client-edit-health" className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="strong">Strong</SelectItem>
                <SelectItem value="stable">Stable</SelectItem>
                <SelectItem value="at_risk">At risk</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Annual revenue (USD)">
            <Input
              data-testid="client-edit-revenue"
              type="number"
              inputMode="decimal"
              value={form.annual_revenue}
              onChange={(e) => setForm({ ...form, annual_revenue: e.target.value })}
              className="rounded-none bg-secondary/40"
              placeholder="Optional"
              disabled={loading}
            />
          </Field>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="client-edit-cancel" className="rounded-none">Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="client-edit-save" className="rounded-none">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">{label}</span>
      {children}
    </label>
  );
}

export default ClientEditDialog;
